export type AuthTransition =
  | "login"
  | "get-email-confirmation"
  | "get-reset-code"
  | "authenticated"
  | "loggedout"
  | "forgot-password"
  | "new-account"
  | "logout";

export type AuthMessageLevel = "error" | "success";

export type AuthEvent =
  | { type: "transition"; state: AuthTransition; email?: string }
  | { type: "message"; text: string; level: AuthMessageLevel };

const LOCAL_ONLY_TRANSITIONS: AuthTransition[] = [
  "login",
  "get-email-confirmation",
  "get-reset-code",
  "forgot-password",
  "new-account",
  "logout",
];

class AuthEventBus {
  private listeners: ((event: AuthEvent) => void)[] = [];

  private channel: BroadcastChannel | undefined;

  constructor() {
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      this.channel = new BroadcastChannel("gac:auth");

      this.channel.onmessage = (e) => {
        if (!e.data || typeof e.data !== "object") return;
        if (!("type" in e.data)) return;

        this.emitLocal(e.data);
      };
    }
  }

  private emitLocal(event: AuthEvent) {
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch (e) {
        console.error("AuthBus listener error", e);
      }
    });
  }

  emit(event: AuthEvent) {
    this.emitLocal(event);

    // Only broadcast transitions that are not local-only
    if (event.type === "transition" && LOCAL_ONLY_TRANSITIONS.includes(event.state)) return;

    this.broadcastAuthEvent(event);
  }

  // fan-out to other runtimes
  broadcastAuthEvent(event: AuthEvent) {
    if (!this.channel) return;

    // console.log("Broadcasting auth event", event);
    this.channel.postMessage(event);
  }

  emitTransition(state: AuthTransition, email?: string) {
    this.emit({ type: "transition", state, email });
  }

  emitMessage(text: string, level: AuthMessageLevel) {
    this.emit({ type: "message", text, level });
  }

  subscribe(listener: (event: AuthEvent) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }
}

export const authBus = new AuthEventBus();
